import { useState, useRef, useCallback } from 'react'
import { getExportStatus, downloadExport } from '@/entities/admin'
import { toast } from 'react-toastify'

type ExportState = 'idle' | 'polling' | 'downloading' | 'done' | 'error'

export function useExport() {
    const [state, setState] = useState<ExportState>('idle')
    const [jobId, setJobId] = useState<string | null>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const cleanup = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const poll = useCallback((id: string) => {
        getExportStatus(id)
            .then((data) => {
                if (data.status === 'DONE' && data.download_ready) {
                    setState('downloading')
                    downloadExport(id)
                        .then(() => {
                            setState('done')
                            setTimeout(() => setState('idle'), 2000)
                        })
                        .catch((err) => {
                            if (err?.response?.status === 409) {
                                toast.info('Файл ещё готовится, ожидайте...')
                                timerRef.current = setTimeout(() => poll(id), 3000)
                                return
                            }
                            if (err?.response?.status === 410) {
                                toast.error('Файл просрочен. Запустите экспорт заново.')
                                setState('error')
                                return
                            }
                            toast.error('Ошибка скачивания')
                            setState('error')
                        })
                } else if (data.status === 'FAILED') {
                    toast.error('Экспорт завершился с ошибкой. Попробуйте снова.')
                    setState('error')
                } else {
                    // PENDING or RUNNING – keep polling
                    timerRef.current = setTimeout(() => poll(id), 3000)
                }
            })
            .catch((err) => {
                if (err?.response?.status === 404) {
                    toast.error('Задача экспорта не найдена')
                } else {
                    toast.error('Ошибка проверки статуса экспорта')
                }
                setState('error')
            })
    }, [])

    const startExport = useCallback(
        (exportResponse: any) => {
            cleanup()
            const id =
                exportResponse?.job_id ||
                exportResponse?.id ||
                exportResponse?.status_url?.split('/').pop()
            if (!id) {
                toast.error('Не удалось получить ID задачи экспорта')
                return
            }
            setJobId(id)
            setState('polling')
            toast.info('Экспорт запущен, ожидайте...')
            timerRef.current = setTimeout(() => poll(id), 2000)
        },
        [cleanup, poll]
    )

    const reset = useCallback(() => {
        cleanup()
        setState('idle')
        setJobId(null)
    }, [cleanup])

    return { exportState: state, jobId, startExport, reset }
}
